import { useState } from "react";
import { api } from "@/lib/api";
import {
  Copy,
  Check,
  Terminal,
  Wifi,
  Shield,
  Rocket,
  Plus,
  CheckCircle2,
  Trash2,
} from "lucide-react";

interface Registration {
  token: string;
  deviceName: string;
  status: string;
  createdAt: string;
  expiresAt?: number;
  usedAt?: string;
  curlCommand?: string;
  sshUser?: string;
  sshPassword?: string;
}

export function SetupPage() {
  const [showRegister, setShowRegister] = useState(false);
  const [deviceName, setDeviceName] = useState("");
  const [creating, setCreating] = useState(false);
  const [newRegistration, setNewRegistration] = useState<Registration | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleRegister = async () => {
    if (!deviceName.trim()) return;
    setCreating(true);
    setError(null);

    const name = deviceName.startsWith("pistack-") ? deviceName : `pistack-${deviceName}`;

    try {
      const { data } = await api.post("/registrations", { deviceName: name });
      setNewRegistration(data);
      setShowRegister(false);
      setDeviceName("");
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="px-8 pt-8 pb-12 max-w-3xl">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-surface-900">Setup Guide</h1>
          <p className="mt-1 text-sm text-surface-500">
            Register and provision Raspberry Pi devices into the PiStack fleet.
          </p>
        </div>
        <button
          onClick={() => { setShowRegister(true); setNewRegistration(null); setError(null); }}
          className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-surface-950 text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors cursor-pointer"
        >
          <Plus size={16} />
          Register Device
        </button>
      </div>

      {/* Registration dialog */}
      {showRegister && (
        <div className="mt-6 p-5 rounded-xl bg-surface-0 border border-brand-200">
          <h3 className="text-sm font-semibold text-surface-900 mb-3">Register New Device</h3>
          <p className="text-sm text-surface-500 mb-4">
            Enter a name for the device. This creates a one-time provisioning URL that expires in 30 minutes.
          </p>
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-surface-400 font-mono">pistack-</span>
              <input
                type="text"
                value={deviceName.replace(/^pistack-/, "")}
                onChange={(e) => setDeviceName(e.target.value.replace(/[^a-zA-Z0-9_-]/g, ""))}
                placeholder="living-room-pi"
                className="w-full pl-[72px] pr-3 py-2 bg-surface-50 border border-surface-200 rounded-lg text-sm text-surface-900 font-mono placeholder:text-surface-400 focus:outline-none focus:border-brand-400"
                onKeyDown={(e) => e.key === "Enter" && handleRegister()}
                autoFocus
              />
            </div>
            <button
              onClick={handleRegister}
              disabled={creating || !deviceName.trim()}
              className="px-4 py-2 bg-brand-500 text-surface-950 text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {creating ? "Creating..." : "Create"}
            </button>
            <button
              onClick={() => { setShowRegister(false); setError(null); }}
              className="px-4 py-2 text-sm text-surface-500 hover:text-surface-700 rounded-lg hover:bg-surface-100 transition-colors cursor-pointer"
            >
              Cancel
            </button>
          </div>
          {error && (
            <p className="mt-3 text-sm text-danger-500">{error}</p>
          )}
        </div>
      )}

      {/* Generated curl command + SSH credentials */}
      {newRegistration && (
        <div className="mt-6 p-5 rounded-xl bg-surface-0 border border-success-500/30">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 size={18} className="text-success-500" />
            <h3 className="text-sm font-semibold text-surface-900">
              Device Registered: {newRegistration.deviceName}
            </h3>
          </div>
          <p className="text-sm text-surface-500 mb-3">
            SSH into your Raspberry Pi and run this command. The URL is single-use and expires in 30 minutes.
          </p>
          <CodeBlock>{newRegistration.curlCommand ?? ""}</CodeBlock>

          <div className="mt-4 p-4 rounded-lg bg-surface-50 border border-surface-200">
            <h4 className="text-xs font-semibold text-surface-700 uppercase tracking-wider mb-3">SSH Credentials (set during provisioning)</h4>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <div className="text-[11px] text-surface-400 mb-1">Username</div>
                <div className="text-sm font-mono text-surface-900">{newRegistration.sshUser ?? "pi"}</div>
              </div>
              <div>
                <div className="text-[11px] text-surface-400 mb-1">Password</div>
                <div className="text-sm font-mono text-surface-900">{newRegistration.sshPassword ?? "—"}</div>
              </div>
              <div>
                <div className="text-[11px] text-surface-400 mb-1">Expires</div>
                <div className="text-sm text-surface-900">{new Date(newRegistration.expiresAt ?? "").toLocaleString()}</div>
              </div>
            </div>
            <p className="mt-3 text-xs text-surface-400">
              These credentials are available in the registration list below. The password was set on the device during provisioning.
            </p>
          </div>
        </div>
      )}

      {/* How it works */}
      <div className="mt-10">
        <h2 className="text-lg font-semibold text-surface-900 mb-6">How It Works</h2>

        <div className="space-y-6">
          <Step number={1} icon={Plus} title="Register the device"
            description='Click "Register Device" above and give it a name. This creates a one-time provisioning token and generates a curl command.' />

          <Step number={2} icon={Terminal} title="Run on the Pi"
            description="SSH into your Raspberry Pi and paste the curl command. The script installs Node.js, the PiStack agent, and runs fleet provisioning — all automatically.">
            <InfoCard>
              The provisioning URL works exactly once. After the script is downloaded, the token
              is immediately invalidated. If the URL expires or is used, you can register the device again.
            </InfoCard>
          </Step>

          <Step number={3} icon={Shield} title="Secure provisioning"
            description="The agent presents a temporary claim certificate to AWS IoT Core, exchanges it for a unique device certificate, and registers as a managed thing. A pre-provisioning hook validates the device name against your registration.">
            <InfoCard>
              Only pre-registered device names are accepted. The claim certificate is embedded
              in the bootstrap script and deleted from the device after provisioning.
            </InfoCard>
          </Step>

          <Step number={4} icon={Wifi} title="Device comes online"
            description="The agent reconnects with its permanent certificate, reports system metrics (CPU, memory, disk, OS), and starts heartbeating every 60 seconds. You'll see it on the Fleet Dashboard within 30 seconds." />

          <Step number={5} icon={Rocket} title="Deploy apps"
            description="Upload artifacts to S3 and trigger deploys via the API. The agent handles shell deployments (tarball extraction + npm install + systemd restart) and container deployments (Docker installed on-demand)." />
        </div>
      </div>

      <div className="mt-8 p-5 rounded-xl bg-surface-0 border border-surface-200">
        <h3 className="text-sm font-semibold text-surface-800 mb-3">Troubleshooting</h3>
        <div className="space-y-3 text-sm text-surface-500">
          <div>
            <span className="font-medium text-surface-700">Token expired or already used: </span>
            Register the device again to get a fresh provisioning URL.
          </div>
          <div>
            <span className="font-medium text-surface-700">Bootstrap fails at provisioning: </span>
            Ensure the Pi can reach AWS IoT Core on port 8883 (outbound TLS). Check{" "}
            <code className="text-brand-400 font-mono text-xs">journalctl -u pistack-agent -n 50</code>.
          </div>
          <div>
            <span className="font-medium text-surface-700">Pre-provisioning hook rejects device: </span>
            The device name must match a registration created in this UI. If you renamed the device
            or the registration expired, create a new one.
          </div>
          <div>
            <span className="font-medium text-surface-700">Device shows offline: </span>
            Check <code className="text-brand-400 font-mono text-xs">systemctl status pistack-agent</code>.
            Offline means no heartbeat received in 2 minutes.
          </div>
        </div>
      </div>

      <div className="mt-4 p-5 rounded-xl bg-surface-0 border border-surface-200">
        <h3 className="text-sm font-semibold text-surface-800 mb-3">Security</h3>
        <div className="space-y-3 text-sm text-surface-500">
          <div>
            <span className="font-medium text-surface-700">One-time URLs: </span>
            Each provisioning URL is single-use and expires in 30 minutes. The claim certificate
            is never stored in a public location.
          </div>
          <div>
            <span className="font-medium text-surface-700">Pre-registration required: </span>
            The IoT Core pre-provisioning hook rejects any device name that hasn't been registered
            through this admin interface.
          </div>
          <div>
            <span className="font-medium text-surface-700">Device isolation: </span>
            Each device gets a unique X.509 certificate. MQTT topics are scoped per-device —
            no cross-device access.
          </div>
        </div>
      </div>

      <div className="mt-4 p-5 rounded-xl bg-surface-0 border border-surface-200">
        <div className="flex items-center gap-2 mb-3">
          <Trash2 size={16} className="text-surface-500" strokeWidth={1.8} />
          <h3 className="text-sm font-semibold text-surface-800">Uninstall Agent</h3>
        </div>
        <p className="text-sm text-surface-500 mb-4">
          The easiest way to uninstall is from the <span className="font-medium text-surface-700">Devices</span> page —
          expand any device and click <span className="font-medium text-surface-700">Uninstall &amp; Remove Device</span>.
          This sends an uninstall command to the Pi and cleans up all AWS resources automatically.
        </p>
        <div className="p-4 rounded-lg bg-surface-50 border border-surface-200">
          <h4 className="text-xs font-semibold text-surface-700 uppercase tracking-wider mb-3">Manual Uninstall</h4>
          <p className="text-sm text-surface-500 mb-3">
            If the device is offline or unreachable from the dashboard, SSH into the Pi and run:
          </p>
          <CodeBlock>curl -sSL https://raw.githubusercontent.com/syncstackai/ss-pistackmanager/main/scripts/uninstall.sh | sudo bash</CodeBlock>
          <p className="text-sm text-surface-500 mt-3 mb-2">
            Or skip the hostname prompt with <code className="text-brand-400 font-mono text-xs">--force</code>:
          </p>
          <CodeBlock>curl -sSL https://raw.githubusercontent.com/syncstackai/ss-pistackmanager/main/scripts/uninstall.sh | sudo bash -s -- --force</CodeBlock>
          <div className="mt-4 text-sm text-surface-500 space-y-2">
            <div>
              <span className="font-medium text-surface-700">What it removes: </span>
              pistack-agent and pistack-shell systemd services, the entire{" "}
              <code className="text-brand-400 font-mono text-xs">/opt/pistack/</code> directory
              (agent, certificates, config, deployed apps, logs), and temporary files.
              Optionally restores the hostname to <code className="text-brand-400 font-mono text-xs">raspberrypi</code>.
            </div>
            <div>
              <span className="font-medium text-surface-700">What it keeps: </span>
              Node.js (may be used by other software) and the SSH password set during provisioning
              (change it manually with <code className="text-brand-400 font-mono text-xs">sudo passwd pi</code>).
            </div>
            <div>
              <span className="font-medium text-surface-700">AWS cleanup: </span>
              The manual script only cleans up the device itself. You'll also need to remove the device
              from the Devices page, or delete the IoT thing via the AWS CLI:{" "}
              <code className="text-brand-400 font-mono text-xs">aws iot delete-thing --thing-name pistack-your-device</code>.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Step({
  number, icon: Icon, title, description, children,
}: {
  number: number; icon: React.ElementType; title: string; description: string; children?: React.ReactNode;
}) {
  return (
    <div className="relative pl-12">
      <div className="absolute left-0 top-0 w-8 h-8 rounded-full bg-brand-100 text-brand-400 flex items-center justify-center text-sm font-bold">
        {number}
      </div>
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Icon size={16} className="text-brand-500" strokeWidth={1.8} />
          <h3 className="text-base font-semibold text-surface-900">{title}</h3>
        </div>
        <p className="text-sm text-surface-500 mb-3">{description}</p>
        {children}
      </div>
    </div>
  );
}

function CodeBlock({ children }: { children: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(children.trim());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group">
      <pre className="bg-surface-50 border border-surface-200 rounded-lg px-4 py-3 text-sm font-mono text-surface-700 overflow-x-auto leading-relaxed">
        {children.trim()}
      </pre>
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 p-1.5 rounded-md bg-surface-200 text-surface-500 hover:text-surface-700 opacity-0 group-hover:opacity-100 transition-all cursor-pointer"
        title="Copy"
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
      </button>
    </div>
  );
}

function InfoCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-4 py-3 rounded-lg bg-blue-100 border border-blue-400/20 text-sm text-surface-600 leading-relaxed">
      {children}
    </div>
  );
}
